import actions from 'actions';
import classNames from 'classnames';
import useCore from 'hooks/useCore';
import DataElements from 'constants/dataElement';
import DataElementWrapper from '../DataElementWrapper';
import { getAnnotationPopupPositionBasedOn } from 'helpers/getPopupPosition';
import { isMobileSize } from 'helpers/getDeviceSize';
import LinkAnnotationPopup from './LinkAnnotationPopup';
import PropTypes from 'prop-types';
import selectors from 'selectors';
import getLinkDestination from 'helpers/getLinkDestination';
import useOnClickOutside from 'hooks/useOnClickOutside';
import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect
} from 'react';
import {
  useSelector,
  shallowEqual,
  useDispatch,
  useStore
} from 'react-redux';
import getGroupedLinkAnnotations from 'src/helpers/getGroupedLinkAnnotations';

const { Annotations } = window.Core;

const propTypes = {
  annotation: PropTypes.object,
  handleOnMouseEnter: PropTypes.func,
  handleOnMouseLeave: PropTypes.func,
};

export const deleteLinkAnnotationWithGroup = (annotation, activeDocumentViewerKey = 1, core) => {
  const annotationManager = core.getAnnotationManager(activeDocumentViewerKey);
  const textHighlightAnnotation = annotationManager.getGroupAnnotations(annotation).find((annot, index) => annot instanceof Annotations.TextHighlightAnnotation && annot.Opacity === 0 && index === 0);
  const linkAnnotations = getGroupedLinkAnnotations(annotation);
  linkAnnotations.forEach((linkAnnot) => {
    annotationManager.ungroupAnnotations([linkAnnot]);
    if (textHighlightAnnotation) {
      annotationManager.deleteAnnotation(linkAnnot, { 'source': 'unlink' }, true);
    }
  });
  if (textHighlightAnnotation) {
    annotationManager.deleteAnnotation(textHighlightAnnotation, { 'source': 'unlink' }, true);
  }
  annotationManager.deleteAnnotation(annotation, { 'source': 'unlink' }, true);
};

const LinkAnnotationPopupContainer = ({
  annotation,
  handleOnMouseEnter,
  handleOnMouseLeave
}) => {
  const { core } = useCore();
  const [
    isOpen,
    activeDocumentViewerKey,
  ] = useSelector((state) => [
    selectors.isElementOpen(state, DataElements.LINK_ANNOTATION_POPUP),
    selectors.getActiveDocumentViewerKey(state),
  ], shallowEqual);

  const [position, setPosition] = useState({ left: 0, top: 0 });
  const dispatch = useDispatch();
  const popupRef = useRef(null);
  const store = useStore();

  const closePopup = () => dispatch(actions.closeElement(DataElements.LINK_ANNOTATION_POPUP));

  const handleMouseMove = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  useOnClickOutside(popupRef, () => {
    if (isOpen) {
      closePopup();
    }
  });

  useEffect(() => {
    const onScroll = () => {
      closePopup();
    };

    const scrollViewElement = core.getScrollViewElement();
    scrollViewElement?.addEventListener('scroll', onScroll);
    return () => {
      scrollViewElement?.removeEventListener('scroll', onScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (annotation || isOpen) {
      setPopupPosition();
    }
  }, [annotation, isOpen, activeDocumentViewerKey]);

  const setPopupPosition = () => {
    if (annotation && popupRef.current) {
      setPosition(getAnnotationPopupPositionBasedOn(annotation, popupRef, activeDocumentViewerKey, 5));
    }
  };

  const contents = getLinkDestination(annotation, store) || '';

  const handleUnLink = () => {
    deleteLinkAnnotationWithGroup(annotation, activeDocumentViewerKey, core);
    closePopup();
    handleOnMouseLeave();
  };

  const className = classNames({
    Popup: true,
    LinkAnnotationPopupContainer: true,
    open: isOpen,
    closed: !isOpen,
  });

  const isAnnotation = annotation !== undefined;

  return (
    <DataElementWrapper
      dataElement={DataElements.LINK_ANNOTATION_POPUP}
      className={className}
      style={{ ...position }}
      ref={popupRef}
    >
      <LinkAnnotationPopup
        linkText={contents}
        handleUnLink={handleUnLink}
        isAnnotation={isAnnotation}
        isMobileDevice={isMobileSize()}
        handleOnMouseEnter={handleOnMouseEnter}
        handleOnMouseLeave={handleOnMouseLeave}
        handleMouseMove={handleMouseMove}
      />
    </DataElementWrapper>

  );
};

LinkAnnotationPopupContainer.propTypes = propTypes;

export default LinkAnnotationPopupContainer;